// src/components/DonationBlock.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
// import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore'; 
// import { formatTimestamp } from '../App'; // formatTimestamp no se usa aquí
import DonationBubble from './DonationBubble';

const hexToRgba = (hex, alpha) => {
    let r = 0, g = 0, b = 0;
    
    if (hex.length === 4) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
    } 
    else if (hex.length === 7) {
        r = parseInt(hex.substring(1, 3), 16);
        g = parseInt(hex.substring(3, 5), 16);
        b = parseInt(hex.substring(5, 7), 16);
    } else {
        r = 31; g = 41; b = 55; 
    }

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

//                  ¡Añadido storyId!
const DonationBlock = ({ authorProfile, chapter, loggedInUserProfile, storyId }) => {
    const [{ isPending }] = usePayPalScriptReducer(); 
    const [error, setError] = useState(null);
    const [succeeded, setSucceeded] = useState(false);
    const [amount, setAmount] = useState('5.00');
    const [message, setMessage] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [recentDonations, setRecentDonations] = useState([]);
    const [isAnonymousChoice, setIsAnonymousChoice] = useState(false);
    const [expandedBubbleId, setExpandedBubbleId] = useState(null);
    const hideBubbleTimeoutRef = useRef(null); // Timer para ocultar bubbles

    useEffect(() => {
        if (!authorProfile?.uid || !chapter?.id) return; 

        const q = query(
            collection(db, "donations"), 
            where("chapterId", "==", chapter.id), 
            orderBy("createdAt", "desc"), 
            limit(10)
        );

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const donations = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRecentDonations(donations);
            console.log("[DonationBlock] Donaciones recientes cargadas:", donations);
        }, (err) => {
            console.error("[DonationBlock] Error al cargar donaciones:", err);
        });

        return () => unsubscribe(); 

    }, [authorProfile?.uid, chapter?.id]);

    const handleMouseEnterBubble = useCallback((donationId) => {
        // Si hay un timer para ocultar, cancelarlo
        if (hideBubbleTimeoutRef.current) {
            clearTimeout(hideBubbleTimeoutRef.current);
            hideBubbleTimeoutRef.current = null;
        }
        // Establece la burbuja actual como expandida (cierra cualquier otra)
        setExpandedBubbleId(donationId);
    }, []);

    const handleMouseLeaveBubble = useCallback(() => {
        // Iniciar un timer para ocultar después de 3 segundos
        hideBubbleTimeoutRef.current = setTimeout(() => {
            setExpandedBubbleId(null); // Oculta la burbuja activa
            hideBubbleTimeoutRef.current = null;
        }, 1000);
    }, []);

    // Limpia el timer si el DonationBlock se desmonta
    useEffect(() => {
        return () => {
            if (hideBubbleTimeoutRef.current) {
                clearTimeout(hideBubbleTimeoutRef.current);
            }
        };
    }, []);

    // --- LÓGICA DE PAGO CON PAYPAL (Sin cambios) ---
    const createOrder = async (data, actions) => {
        setError(null);
        setIsProcessing(true);
        console.log("Creando orden:", amount, "USD para", authorProfile.uid, "capítulo:", chapter.id);
        try {
            const functions = getFunctions();
            const createOrderFunc = httpsCallable(functions, 'createPayPalDonationOrder');
            const result = await createOrderFunc({
                amount: amount,
                currency: 'USD',
                creatorUid: authorProfile.uid,
                chapterId: chapter.id
            });
            
            if (result.data.orderID) {
                console.log("Order ID creado:", result.data.orderID);
                setIsProcessing(false);
                return result.data.orderID;
            } else {
                throw new Error("El backend no devolvió un orderID.");
            }
        } catch (err) {
            console.error("Error al crear orden en backend:", err);
            setError(err.message || "No se pudo iniciar el pago.");
            setIsProcessing(false);
            return null;
        }
    };

    const onApprove = async (data, actions) => {
        setError(null);
        setIsProcessing(true);
        console.log("Aprobado. Capturando:", data.orderID, "Msg:", message, "Anon:", isAnonymousChoice, "UID:", loggedInUserProfile?.uid);
        try {
            const functions = getFunctions();
            const captureOrderFunc = httpsCallable(functions, 'capturePayPalDonationOrder');
            
            const result = await captureOrderFunc({ 
                orderID: data.orderID,
                message: message,
                donorUid: loggedInUserProfile?.uid || null,
                isAnonymousChoice: isAnonymousChoice
            });

            if (result.data.status === 'COMPLETED') {
                console.log("Captura completada con éxito:", result.data);
                setSucceeded(true);
                setIsPopupOpen(false);
                setMessage('');
                setTimeout(() => setSucceeded(false), 5000);
                setIsAnonymousChoice(false);
            } else {
                console.warn("Captura completada pero estado no es COMPLETED:", result.data);
                setError(`El pago fue procesado pero con estado: ${result.data.status}`);
            }
        } catch (err) {
            console.error("Error al capturar orden en backend:", err);
            setError(err.message || "No se pudo confirmar tu donación.");
        } finally {
            setIsProcessing(false);
        }
    };

    const onError = (err) => {
        console.error("Error de PayPalButtons:", err);
        setError("Ocurrió un error inesperado con PayPal. Intenta de nuevo.");
        setIsProcessing(false);
    };

    const onCancel = (data) => {
        console.log("Pago cancelado por el usuario:", data);
        setError("Has cancelado el proceso de donación.");
        setIsProcessing(false);
    };

    // --- ¡NUEVA LÓGICA DE PAGO CON STRIPE! ---
    const handleStripeCheckout = async () => {
        setError(null);
        setIsProcessing(true);

        // Validar monto
        const numericAmount = parseFloat(amount);
        if (isNaN(numericAmount) || numericAmount < 1.00) {
            setError("Por favor, introduce un monto válido (mínimo $1.00).");
            setIsProcessing(false);
            return;
        }

        console.log("Iniciando checkout de Stripe...");
        try {
            const functions = getFunctions();
            const createCheckoutFunc = httpsCallable(functions, 'createStripeCheckoutSession');
            
            const result = await createCheckoutFunc({
                authorStripeId: authorProfile.donations.stripeAccountId,
                amount: numericAmount,
                message: message,
                chapterId: chapter.id,
                storyId: storyId, // <-- Prop de storyId
                isAnonymous: isAnonymousChoice
            });

            // Gracias al cambio en index.js, ahora recibimos la URL
            if (result.data.url) {
                console.log("Redirigiendo a Stripe Checkout...");
                // Redirige al usuario a la página de pago de Stripe
                window.location.href = result.data.url;
            } else {
                throw new Error("No se recibió la URL de Stripe.");
            }
        } catch (err) {
            console.error("Error al crear sesión de Stripe:", err);
            setError(err.message || "No se pudo iniciar el pago con Stripe.");
            setIsProcessing(false); // Libera el botón si hay error
        }
        // No ponemos 'finally' aquí, porque si tiene éxito, la página redirige.
    };
    
    // --- NUEVA FUNCIÓN PARA RENDERIZAR BOTONES ---
    const renderPaymentButtons = () => {
        const provider = authorProfile?.donations?.provider;
        const isDisabled = !amount || parseFloat(amount) < 1.00 || isProcessing || isPending;

        switch (provider) {
            // Caso 1: Autor usa PayPal
            case 'paypal':
                return (
                    <>
                        {isPending && <div className="text-center py-4">Cargando opciones de pago...</div>}
                        <PayPalButtons
                            style={{ layout: 'vertical', color: 'blue', shape: 'rect', label: 'pay' }}
                            createOrder={createOrder} 
                            onApprove={onApprove}     
                            onError={onError}         
                            onCancel={onCancel}       
                            disabled={isDisabled} 
                            forceReRender={[amount, message]}
                        />
                    </>
                );

            // Caso 2: Autor usa Stripe
            case 'stripe':
                return (
                    <button
                        onClick={handleStripeCheckout}
                        disabled={isDisabled}
                        className="w-full px-6 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isProcessing ? "Procesando..." : `Pagar $${parseFloat(amount).toFixed(2)} con Tarjeta`}
                    </button>
                );

            // Caso 3: Fallback (no debería pasar si showDonationBlock es correcto)
            default:
                return <p className="text-red-400 text-center">Error: No se ha configurado un proveedor de pagos válido para este autor.</p>;
        }
    };


    const widgetConfig = authorProfile?.donations?.widgetConfig || {};
    const ctaMessage = widgetConfig.ctaMessage || `Apoya a @${authorProfile.username}`;
    const buttonText = widgetConfig.buttonText || "Donar";
    const userHexColor = widgetConfig.backgroundColor || '#1F2937';
    const fixedAlpha = 0.6;
    const blurAmount = '42px';
    const rgbaBackgroundColor = hexToRgba(userHexColor, fixedAlpha);
    const showAmountInBubbles = widgetConfig.showAmountInBubbles !== false;

    return (
        <div
            style={{
                backgroundColor: rgbaBackgroundColor,
                backdropFilter: `blur(${blurAmount})`,
                WebkitBackdropFilter: `blur(${blurAmount})`
            }}
            className="rounded-lg p-6 my-8 text-white shadow-lg relative FONDO_DONO_BLOCK"
        > 
            
            {/* --- VISTA INICIAL (Sin cambios) --- */}
            {!isPopupOpen && (
                <div className="text-center">
                    {succeeded && (
                        <div className="absolute top-0 left-0 right-0 -mt-4 p-2 bg-green-600 text-white text-sm rounded-t-lg animate-fade-in-down">
                            ¡Gracias por tu apoyo! Tu donación fue enviada.
                        </div>
                    )}

                    <h3 className="text-xl font-bold mb-4 DONO_BLOCK_MESSAGE">{ctaMessage}</h3>

                    <button 
                        onClick={() => setIsPopupOpen(true)}
                        className="px-6 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-bold transition-colors"
                    >
                        {buttonText}
                    </button>

                                        {/* Burbujas de donaciones */}
                    <div className="flex flex-wrap justify-center items-center gap-2 mt-9 min-h-[50px]"> 
                        {recentDonations.length === 0 && (
                            <p className="text-sm text-gray-400 italic">Sé el primero en donar.</p>
                        )}
                        {recentDonations.map(donation => (
                            <DonationBubble
                                key={donation.id}
                                donation={donation}
                                showAmount={showAmountInBubbles}
                                // --- Props para manejar estado centralizado ---
                                isExpanded={expandedBubbleId === donation.id} // <-- Decide si esta está expandida
                                onMouseEnterBubble={handleMouseEnterBubble}   // <-- Función a llamar al entrar
                                onMouseLeaveBubble={handleMouseLeaveBubble}   // <-- Función a llamar al salir
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* --- POPUP / MODAL DE PAGO (Modificado) --- */}
            {isPopupOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100] p-4">
                    <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md relative">
                        <button 
                            onClick={() => setIsPopupOpen(false)} 
                            className="absolute top-2 right-2 text-gray-400 hover:text-white"
                            disabled={isProcessing}
                        >
                            &times;
                        </button>

                        <h4 className="text-lg font-semibold mb-4 text-center">Apoya a @{authorProfile.username}</h4>

                         {/* Input Monto */}
                         <div className="mb-4">
                            <label htmlFor="donationAmountPopup" className="block text-sm font-medium text-gray-300 mb-1">Monto (USD):</label>
                            <input 
                                id="donationAmountPopup"
                                type="number" 
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                min="1.00" 
                                step="0.01"
                                className="w-full px-3 py-2 text-gray-900 bg-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="5.00"
                                disabled={isProcessing || isPending} 
                            />
                         </div>

                         {/* Input Mensaje */}
                         <div className="mb-6">
                            <label htmlFor="donationMessagePopup" className="block text-sm font-medium text-gray-300 mb-1">Mensaje (opcional, máx 100 caract.):</label>
                            <input 
                                id="donationMessagePopup"
                                type="text" 
                                value={message}
                                onChange={(e) => setMessage(e.target.value.substring(0, 100))}
                                maxLength="100"
                                className="w-full px-3 py-2 text-gray-900 bg-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                placeholder="¡Gran capítulo!"
                                disabled={isProcessing || isPending} 
                            />
                         </div>

                         {/* Checkbox Anónimo */}
                         <div className="mb-6">
                            <label className="flex items-center text-gray-300 text-sm">
                                <input 
                                    type="checkbox"
                                    checked={isAnonymousChoice}
                                    onChange={(e) => setIsAnonymousChoice(e.target.checked)}
                                    className="mr-2 h-4 w-4 rounded text-indigo-600 focus:ring-indigo-500 border-gray-600 bg-gray-700"
                                    disabled={isProcessing || isPending}
                                />
                                Donar anónimamente (no mostrará tu @usuario ni avatar)
                            </label>
                        </div>

                        {/* --- ¡AQUÍ ESTÁ LA LÓGICA CONDICIONAL! --- */}
                        <div className="payment-buttons-container">
                            {renderPaymentButtons()}
                        </div>

                        {/* Muestra mensajes de error dentro del popup */}
                        {error && <p className="text-red-400 text-sm mt-3 text-center">{error}</p>}
                        {/* Indicador de procesamiento (ahora es genérico) */}
                        {isProcessing && !isPending && <p className="text-blue-400 text-sm mt-3 text-center">Procesando...</p>}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DonationBlock;